import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { ActionPowerInvalidOrderItemModal } from '@/components/domains/order/ActionPowerInvalidOrderItemModal'
import { Order } from '@/components/domains/order2/order'
import ROUTES from '@/constants/legacy/constRoutes'
import { OrderItemInputType } from '@/constants/legacy/constType'
import { OrderTypeEnum } from '@/constants/order-type.enum'
import MUTATION_VARIABLES from '@/containers/gqls/base/mutation_variables'
import { useApiMutation, useCustomRouter, useUserInfo } from '@/containers/hooks'
import { useGetEstimateOrder } from '@/containers/order/get-estimate-order.hook'
import { useInvalidConditionBeforeEstimateOrder } from '@/containers/order/invalid-condition-before-estimate-order.hook'
import { UserContainer } from '@/containers/users/UserContainer'
import { doSetEstimateOrderRequestData } from '@/stores/reduxData'
import { store } from '@/stores/store'
import { Base64ConverterUtil } from '@/utils/base64-converter.util'
import { AppCartProductOptionType } from '@/utils/bridge/bridge'
import { isAppBuyNow } from '@/utils/utilBridge'
import { isEmpty } from '@/utils/utilCommon'
import { localEstimateTarget } from '@/utils/utilLocalStorage'

const PageOrder = () => {
  const { replace } = useCustomRouter()
  const { me } = UserContainer.useContainer()
  const { useAuthenticatedRoute } = useUserInfo
  const { routeToAddInfo } = useAuthenticatedRoute()
  const [estimateOrderMutation] = useApiMutation('estimateOrder')
  const dispatch = useDispatch()
  const { estimatedOrderRequest } = useGetEstimateOrder()

  /**
   * 액션 파워 상품은 1개씩만 주문 가능합니다.
   */
  const { isInvalidConditionBeforeEstimateOrderQueryDataLoading, isInvalidOrderItems, actionPowerProducts } =
    useInvalidConditionBeforeEstimateOrder()

  const setAppBuyNowData = async () => {
    localEstimateTarget.remove()
    const orderItemsInput: OrderItemInputType[] = window
      .getBuyNowCartList()
      .map((cartProductOption: AppCartProductOptionType) => ({
        quantity: cartProductOption.purchaseQuantity,
        productOption: cartProductOption.productOptionId,
        productPromotion: cartProductOption.productPromotionId,
      }))
    // @ts-ignore
    const { data } = await estimateOrderMutation({
      variables: MUTATION_VARIABLES.ESTIMATE_ORDER({ orderItems: orderItemsInput }),
    })

    // 모든 shipping edges를 순회하며 order items 추출
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const extractedItems = data.estimateOrder.order.shippings.edges?.flatMap((shippingEdge: any) =>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      shippingEdge.node.shippingOrderItems.edges.map((orderItemEdge: any) => {
        const orderItem = orderItemEdge.node
        return {
          id: Base64ConverterUtil.convertBase64ToNumberId(orderItem.productOption.product.id),
          ...(!!orderItem?.productPromotion?.id && {
            productPromotionId: Base64ConverterUtil.convertBase64ToNumberId(orderItem?.productPromotion?.id),
          }),
          productOptionId: Base64ConverterUtil.convertBase64ToNumberId(orderItem.productOption.id),
          quantity: orderItem.quantity,
        }
      })
    )

    dispatch(
      doSetEstimateOrderRequestData({
        mileage: undefined,
        shippings: [
          {
            products: extractedItems,
          },
        ],
      })
    )

    localEstimateTarget.save({
      mileage: undefined,
      shippings: [
        {
          products: extractedItems,
        },
      ],
    })

    await replace({
      pathname: ROUTES.ORDER.BASE,
      query: {
        orderType: OrderTypeEnum.BuyNow,
      },
    })
  }

  useEffect(() => {
    if (!me) {
      return
    }

    // 기존 코드 복사 - 용도 정확히 파악 안됨
    const _cartShippingGroups = store.getState().reduxDataReducers.estimatedOrderRequest || localEstimateTarget.load()
    if (isAppBuyNow) {
      const appBuyNowCartList = window.getBuyNowCartList()
      if (isEmpty(appBuyNowCartList)) {
        if (isEmpty(_cartShippingGroups)) {
          replace(ROUTES.CART)
          return
        }
      } else {
        setAppBuyNowData()
      }
    } else {
      if (isEmpty(_cartShippingGroups)) {
        replace(ROUTES.CART)
        return
      }
    }

    if (!me.mobileNumber) {
      routeToAddInfo(undefined, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me])

  if (!me || isInvalidConditionBeforeEstimateOrderQueryDataLoading) {
    return null
  }

  if (isInvalidOrderItems && actionPowerProducts) {
    return (
      <ActionPowerInvalidOrderItemModal
        isInvalidOrderItems={isInvalidOrderItems}
        actionPowerProducts={actionPowerProducts}
      />
    )
  }

  return <>{estimatedOrderRequest && <Order estimatedOrderRequest={estimatedOrderRequest} />}</>
}

export default PageOrder
